import { Component, OnInit, ElementRef, ViewChild, forwardRef, Input, SimpleChanges, OnChanges, HostListener, HostBinding, Output, EventEmitter, Inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil, set } from 'lodash-es';
import { Styles } from './models/style';
import { SelectorComponent } from './components/selector.component';
import { ESelectorType } from './components/selector.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { PrintNode_ShellService } from './PrintNode.shell.service';
import { PrintNode_OperationService } from './PrintNode.operation.service';
import { PrintNode_DatasourceService } from './PrintNode.datasource.index';
import { PrintNode_FlowService } from './PrintNode.flow.index';
import { PrintNode_ReportService } from './PrintNode.report.index';
import { PrintNode_LocalizationService } from './PrintNode.localization.service';
import { PrintNode_scales_dd_ComponentContextService } from './PrintNode.scales_dd.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './PrintNode.frontend.types'
import { $frontendTypes as $types} from './PrintNode.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'PrintNode-scales_dd_multi',
  templateUrl: './PrintNode.scales_dd_multi.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrintNode_scales_dd_multiComponent),
      multi: true
    }
  ]
})
export class PrintNode_scales_dd_multiComponent extends BaseComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() type?: ESelectorType;
  _disabled = false;
  @Input() get disabled() { 
      return this._disabled 
      ; }
  set disabled(val: any) { this._disabled = val; }

  @Input() placeholder = '';
  @Input() styles: Styles;
  @Input() tooltip = '';

  @Output() displayTextChange: EventEmitter<string> = new EventEmitter();

  @ViewChild(SelectorComponent) selector: SelectorComponent;

  @HostBinding('tabIndex') get tabIndex() { return -1; }
  @HostListener('focus')
  focus() {
    this.selector?.focus();
  }

  get isMulti() {
    return true;
  }

  get isSingle() {
    return false;
  }


  inParams: { entity_type?: string, entity_id?: string } = { entity_type: null, entity_id: null };
  @Input('entity_type') set $inParams_entity_type(value: any) {
    this.inParams['entity_type'] = value;
  }
  get $inParams_entity_type(): any {
    return this.inParams['entity_type'] ;
  }
  @Input('entity_id') set $inParams_entity_id(value: any) {
    this.inParams['entity_id'] = value;
  }
  get $inParams_entity_id(): any {
    return this.inParams['entity_id'] ;
  }

  fullTextSearch: string;

  constructor(
    private $utils: UtilsService,
    private $settings: SettingsValuesService,
    private $shell: PrintNode_ShellService,
    private $datasources: PrintNode_DatasourceService,
    private $flows: PrintNode_FlowService,
    private $reports: PrintNode_ReportService,
    private $localization: PrintNode_LocalizationService,
    private $operations: PrintNode_OperationService,
    private $logger: CleanupLoggerService,
    private $context: PrintNode_scales_dd_ComponentContextService,
    private _elementRef: ElementRef
    ) {
      super();
  }

  ngOnInit(): void {
    this.$init();
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$init();
    }
  }


  async $init() {
    this.selector?.init();
    await this.on_init();
  }

  onKeyDown($event: KeyboardEvent) {
    //TODO:KeyDown    
  }

  //ControlValueAccessor
  _innerValue: any;

  onChange: any = () => { };
  onTouch: any = () => { };

  get value(): string | string[] {
    return this._innerValue;
  }

  set value(val:  string | string[]) {
    if (this._innerValue !== val) {
      this._innerValue = val;
      this.onChange(val);
      this.onTouch(val);
    }
  }

  writeValue(value: any): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  displayWithFn(value: any): Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}> {
    const promise = new Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = null;
      const $fullTextSearch = null;
      this.$datasources.PrintNode.ds_get_scales.getByKeys({
        $keys: value,
        entity:  $utils.isAllDefined($selector.inParams.entity_id, $selector.inParams.entity_type) ? { type: $selector.inParams.entity_type, id: $selector.inParams.entity_id} : undefined ,
        full_text_search:  $selector.fullTextSearch ,
      }).then(result => {
        const selected = value.map(k => {
          const found = result.result
            .find(i => i.id === k);
          const e: { id?: string, name?: string, computer_id?: string, capture?: { micrograms?: number, captured_on?: string }, device_number?: number, computer?: { id?: number, name?: string } } = found 
            ?? { 
              id : k
            };

          const $option = { entity: e };
          const $entity = e;
          const $resultOption = { key: null, name: found ? `${$option.entity.name}${$option.entity.device_number > 0 ? `-${$option.entity.device_number}` : ''} ${$utils.isDefined($option.entity.computer?.name) ? `(${$option.entity.computer.name})` : ''}` : 'No data to display', disabled: false, selected:true };
          $resultOption.key = $option.entity.id;
          return $resultOption;
        });
        const displayText = value.map(k => {
          const found = result.result
            .find(i => i.id === k);
          if (found) {
            const $option = { entity: found };
            const $entity = found;
            return `${$option.entity.name}${$option.entity.device_number > 0 ? `-${$option.entity.device_number}` : ''} ${$utils.isDefined($option.entity.computer?.name) ? `(${$option.entity.computer.name})` : ''}`;
          } else {
            return 'No data to display';
          }
        })
        .join(
           ', '
        );
        this.displayTextChange.emit(displayText);
        resolve({displayText, selected});
      });
    });
    return promise;
  }

  optionsFn(filterText: string): Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }> {

    const promise = new Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = filterText;
      const $fullTextSearch = filterText;
      this.$datasources.PrintNode.ds_get_scales.getList({
        $top: 25,
        $skip: 0,
        entity:  $utils.isAllDefined($selector.inParams.entity_id, $selector.inParams.entity_type) ? { type: $selector.inParams.entity_type, id: $selector.inParams.entity_id} : undefined ,
        full_text_search:  $selector.fullTextSearch ,
      }).then(result => {
        resolve({
          list: result.result.map(item => {
            const $option = { entity: item };
            const $entity = item;
            const $resultOption = { key: null, name: `${$option.entity.name}${$option.entity.device_number > 0 ? `-${$option.entity.device_number}` : ''} ${$utils.isDefined($option.entity.computer?.name) ? `(${$option.entity.computer.name})` : ''}`, disabled: null };
            $resultOption.key = $option.entity.id;
            return $resultOption;
          }),
          totalCount: result.totalCount,
          top: 25
        });
      });

    });

    return promise;

  }

  //#region private flows
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $selector: PrintNode_scales_dd_multiComponent,
  
    $shell: PrintNode_ShellService,
    $datasources: PrintNode_DatasourceService,
    $flows: PrintNode_FlowService,
    $reports: PrintNode_ReportService,
    $settings: SettingsValuesService,
    $operations: PrintNode_OperationService,
    $utils: UtilsService,
    $context: PrintNode_scales_dd_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PrintNode_LocalizationService,
    $event: any
  ) {
  // if ($utils.isDefined($selector.inParams.entity) && !$utils.isAllDefined($selector.inParams.entity.id, $selector.inParams.entity.type)) {
  //     $selector.inParams.entity = undefined;
  // }
  }
  //#endregion private flows
}
